import { useState } from 'react';

interface UsePaginationProps<T> {
    items: T[];
    initialPage?: number;
    initialPageSize?: number;
}

interface UsePaginationResult<T> {
    page: number;
    pageSize: number;
    totalCount: number;
    selectedItems: T[];
    paginatedItems: T[];
    currentPageSize: number;
    resetPagination: () => void;
    isAllSelected: () => boolean;
    isIndeterminate: () => boolean;
    setSelectedItems: (items: T[]) => void;
    getItemsForPage: (newPage: number) => T[];
    handleChangePage: (newPage: number) => void;
    handleSelectAll: (checked: boolean) => void;
    handleShowSizeChange: (newPageSize: number) => void;
}

/**
 * Custom hook to handle pagination for any collection of items
 *
 * @param props - The props for the hook
 * @returns Pagination state and handlers
 */
export function usePagination<T>({
    items = [],
    initialPage = 1,
    initialPageSize = 10,
}: UsePaginationProps<T>): UsePaginationResult<T> {
    const [page, setPage] = useState<number>(initialPage);
    const [pageSize, setPageSize] = useState<number>(initialPageSize);
    const [selectedItems, setSelectedItems] = useState<T[]>([]);
    const [currentPageSize, setCurrentPageSize] = useState<number>(0);

    // Function to get the items for the current page
    const getItemsForPage = (newPage: number) => {
        // Calculate the start and end index for the current page
        const startIndex = (newPage - 1) * pageSize;
        const endIndex = Math.min(startIndex + pageSize, items.length);
        // Get the items for the current page
        return items.slice(startIndex, endIndex);
    };

    const itemsForPage = getItemsForPage(initialPage);
    const [paginatedItems, setPaginatedItems] = useState<T[]>(itemsForPage);

    const totalCount = items.length;

    const setPagination = (newPage: number) => {
        const newItems = getItemsForPage(newPage);

        // Set the pagination for the new items
        setPaginatedItems(newItems);
        setCurrentPageSize(newItems.length);
    };

    /**
     * Handle the change of page in pagination
     * @param newPage - The new page number
     */
    const handleChangePage = (newPage: number) => {
        setPage(newPage);
        setPagination(newPage);
    };

    /**
     * Handle the change of page size in pagination
     * @param newPageSize - The new page size
     */
    const handleShowSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPagination(1);
        setPage(1); // Reset to first page when changing page size
    };

    /**
     * Handles the select all checkbox
     * @param checked - Whether the checkbox is checked
     */
    const handleSelectAll = (checked: boolean) => {
        // Get current page items
        const startIndex = (page - 1) * pageSize;
        const endIndex = Math.min(startIndex + pageSize, items.length);
        const currentPageItems = items.slice(startIndex, endIndex);

        if (checked) {
            // Add all current page items to selection (avoid duplicates)
            const updatedSelection = Array.from(new Set([...selectedItems, ...currentPageItems]));
            setSelectedItems(updatedSelection);
            setPaginatedItems(currentPageItems);
            setCurrentPageSize(currentPageItems.length);
        } else {
            const updatedSelection = selectedItems.filter((item) => !currentPageItems.includes(item));
            setSelectedItems(updatedSelection);
        }
    };

    /**
     * Check if all items on the current page are selected
     */
    const isAllSelected = () => {
        const currentPageItems = getItemsForPage(page);
        const selectedItemsOnPage = selectedItems.filter((item) => currentPageItems.includes(item));
        return selectedItemsOnPage.length > 0 && selectedItemsOnPage.length === currentPageItems.length;
    };

    /**
     * Check if some but not all items on the current page are selected
     */
    const isIndeterminate = () => {
        const currentPageItems = getItemsForPage(page);
        const selectedItemsOnPage = selectedItems.filter((item) => currentPageItems.includes(item));
        return selectedItemsOnPage.length > 0 && selectedItemsOnPage.length < currentPageItems.length;
    };

    /**
     * Reset the pagination
     * @returns void
     */
    const resetPagination = () => {
        setPage(1);
        setPageSize(initialPageSize);
        setSelectedItems([]);
        setPaginatedItems(itemsForPage);
        setCurrentPageSize(itemsForPage.length);
    };

    return {
        page,
        pageSize,
        totalCount,
        selectedItems,
        paginatedItems,
        currentPageSize,
        isAllSelected,
        handleSelectAll,
        isIndeterminate,
        handleChangePage,
        setSelectedItems,
        getItemsForPage,
        resetPagination,
        handleShowSizeChange,
    };
}
